class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    function _defineProperty(obj, key, value) {
      if (key in obj) {
        Object.defineProperty(obj, key, {
          value,
          enumerable: true,
          configurable: true,
          writable: true,
        });
      } else {
        obj[key] = value;
      }
      return obj;
    }

    _defineProperty(this, "setPopularSearchesLink", () => {
      const popularSearchItems = document.querySelectorAll('[data-ps-item]');
      popularSearchItems.forEach(itm => itm.href = createSearchLink(itm.dataset.psQuery));
    });

    this.selectors = {
      searchQuery: '[data-query]',
      searchMessage: '[data-message]',
      input: '[data-search-input]',
      submit: 'button[type="submit"]',
      loading: '[data-spinner]',
      clear: '[data-clear-search]',
      popularSearchItems: ['[data-ps-item]'],
      searchResults: '[data-predictive-search]'
    };

    this.container = this.closest('[data-search-container]') || document.querySelector('[data-search-container]');
    this.domNodes = queryDomNodes(this.selectors, this.container);
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.popularSearches = this.querySelector('[data-popular-searches]');
    this.searchCount = this.querySelector('[data-search-count]');

    // Lazy init: defer event listeners and popular links until first focus
    this._initialized = false;
    if (this.input) {
      this.input.addEventListener('focus', () => this._lazyInit(), { once: true });
    }
  }

  _lazyInit() {
    if (this._initialized) return;
    this._initialized = true;
    this.setupEventListeners();
    this.setPopularSearchesLink();
  }

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector('form.m-search-form');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce(event => {
      this.onChange(event);
    }, 300).bind(this));
    clear.addEventListener('click', this.onClearSearch.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = '';
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);
    const searchByTag = this.dataset.searchByTag === 'true';
    const searchByBody = this.dataset.searchByBody === 'true';
    const unavailableProductsOption = this.dataset.unavailableProductsOption;
    let searchFields = 'title,product_type,vendor,variants.sku,variants.title';
    if (searchByTag) searchFields += ',body';
    if (searchByBody) searchFields += ',tag';

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const buildUrl = (basePath) => `${basePath}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    // Bilingual fallback: if current locale returns empty, retry against default (root) locale.
    const currentRoute = window.MinimogSettings.routes.predictive_search_url;
    const rootRoute = '/search/suggest';
    const primaryURL = buildUrl(currentRoute);
    const fetchMarkup = (url) => fetch(url).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.text();
    }).then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      return section ? section.innerHTML : '';
    });

    fetchMarkup(primaryURL).then((markup) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = markup;
      const itemsWrap = tmp.querySelector('[data-search-items-wrapper]');
      const hasResults = itemsWrap && itemsWrap.childElementCount > 0;
      if (hasResults || currentRoute === rootRoute) {
        this.cachedResults[queryKey] = markup;
        this.renderSearchResults(markup);
        return;
      }
      // Fallback: search default locale (catches English product names on /zh-au etc.)
      fetchMarkup(buildUrl(rootRoute)).then((fallbackMarkup) => {
        this.cachedResults[queryKey] = fallbackMarkup;
        this.renderSearchResults(fallbackMarkup);
      }).catch(() => {
        this.cachedResults[queryKey] = markup;
        this.renderSearchResults(markup);
      });
    }).catch((error) => {
      this.close();
      throw error;
    });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    const searchItemsWrapper = this.querySelector('[data-search-items-wrapper]');

    if (searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage } = this.domNodes;
    const query = input.value;
    const { resultsTitle } = searchMessage.dataset;
    searchQuery.textContent = query;

    if (results) {
      searchMessage.textContent = resultsTitle;
    } else {
      searchMessage.textContent = searchMessage.dataset.noResults;
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    submit.style.visibility = show ? 'hidden' : 'visible';
    loading.style.visibility = show ? 'visible' : 'hidden';
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.predictiveSearchResults.classList.remove('hidden');
    this.predictiveSearchResults.classList.add('is-open');
    this.searchCount.classList.remove('hidden');
  }

  close() {
    let clearSearchTerm = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.toggleClearSearch(false);
    }

    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.classList.add('hidden');
    this.predictiveSearchResults.classList.remove('is-open');
    this.popularSearches.classList.remove('hidden');
    this.searchCount.classList.add('hidden');
  }
}

customElements.define('predictive-search', PredictiveSearch);
